// packages/ui/src/components/Combobox/Combobox.tsx

import { useId, useMemo, useRef, useState, useEffect } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { Check, ChevronDown, X } from 'lucide-react';

import { cn } from '../../lib/cn';

export interface ComboboxOption<T = string> {
  value: T;
  label: string;
  /** Secondary label shown on the right (e.g., UF badge next to city name). */
  hint?: string;
  /** When true, option is rendered but not selectable. */
  disabled?: boolean;
}

export interface ComboboxProps<T = string> {
  options: ComboboxOption<T>[];
  value: T | null;
  onChange: (value: T | null) => void;

  label?: string;
  /** Accessible name when there is no visible label (the field is titled outside). */
  ariaLabel?: string;
  placeholder?: string;
  /** Placeholder of the search box inside the panel. Falls back to `placeholder`. */
  searchPlaceholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  helperText?: string;
  error?: string;

  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  clearable?: boolean;

  /**
   * When provided, typing in the search box calls this with the current query
   * on each keystroke. Consumers use it to trigger server-side search.
   * If omitted, the component filters `options` locally by label.
   */
  onSearch?: (query: string) => void;

  /** When true (default), client-side filters `options` by label substring. */
  filterByLabel?: boolean;

  className?: string;
  /** Classes do painel flutuante. Útil quando as opções precisam de mais espaço que o gatilho. */
  panelClassName?: string;
  /** Portal the panel with collision handling when an overflow ancestor would clip it. */
  portalled?: boolean;
  /** Give a portalled panel its own focus and scroll lock when used inside a modal. */
  modal?: boolean;
  /** Conteúdo persistente abaixo das opções; não participa da filtragem nem da navegação cmdk. */
  footer?: ReactNode;
  name?: string;
  id?: string;
  testId?: string;
}

export function Combobox<T extends string>(props: ComboboxProps<T>) {
  const {
    options,
    value,
    onChange,
    label,
    ariaLabel,
    placeholder,
    searchPlaceholder,
    emptyMessage,
    loadingMessage,
    helperText,
    error,
    disabled = false,
    loading = false,
    required = false,
    clearable = true,
    onSearch,
    filterByLabel = true,
    className,
    panelClassName,
    portalled = false,
    modal = false,
    footer,
    name,
    id,
    testId,
  } = props;

  const generatedId = useId();
  const inputId = id ?? `combobox-${generatedId}`;
  const listboxId = `${inputId}-listbox`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedInOptions = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );
  const [retainedSelection, setRetainedSelection] = useState<ComboboxOption<T> | null>(
    selectedInOptions,
  );

  // Remote results are a search window; removing a result does not clear the controlled value.
  useEffect(() => {
    setRetainedSelection((previous) => {
      if (value === null) return null;
      if (!selectedInOptions) return previous?.value === value ? previous : null;
      return previous?.value === selectedInOptions.value &&
        previous.label === selectedInOptions.label &&
        previous.hint === selectedInOptions.hint &&
        previous.disabled === selectedInOptions.disabled
        ? previous
        : selectedInOptions;
    });
  }, [selectedInOptions, value]);

  const selected =
    selectedInOptions ?? (retainedSelection?.value === value ? retainedSelection : null);

  // Close on outside click.
  useEffect(() => {
    if (!open || portalled) return;
    function handleClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, portalled]);

  function handleQueryChange(v: string) {
    setQuery(v);
    if (onSearch) onSearch(v);
  }

  function handleSelect(next: T) {
    setRetainedSelection(options.find((option) => option.value === next) ?? null);
    onChange(next);
    setQuery('');
    onSearch?.('');
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function handlePanelKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    setQuery('');
    onSearch?.('');
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(null);
    setQuery('');
    onSearch?.('');
  }

  const filteredOptions =
    filterByLabel && !onSearch && query
      ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
      : options;

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      id={inputId}
      role="combobox"
      aria-label={ariaLabel ?? label ?? placeholder}
      aria-expanded={open}
      aria-controls={listboxId}
      aria-haspopup="listbox"
      aria-invalid={!!error || undefined}
      aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
      disabled={disabled}
      onClick={portalled ? undefined : () => !disabled && setOpen((current) => !current)}
      name={name}
      data-testid={testId}
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-md border px-3',
        'bg-white text-sm font-body text-left transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-alamo-orange-500 focus:ring-offset-0',
        error ? 'border-alamo-red-500' : 'border-slate-300 hover:border-slate-400',
        disabled && 'cursor-not-allowed bg-slate-50 text-slate-400',
      )}
    >
      <span className={cn('truncate', selected ? 'text-secondary-950' : 'text-slate-600')}>
        {selected ? (
          <>
            {selected.label}
            {selected.hint && <span className="ml-2 text-xs text-slate-500">{selected.hint}</span>}
          </>
        ) : (
          (placeholder ?? ' ')
        )}
      </span>

      <span className="ml-2 flex items-center gap-1">
        {clearable && selected && !disabled && (
          <span
            role="button"
            aria-label="Clear selection"
            onClick={handleClear}
            className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown
          className={cn('h-4 w-4 text-slate-500 transition-transform', open && 'rotate-180')}
        />
      </span>
    </button>
  );

  const panel = (
    <Command
      shouldFilter={false}
      className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
    >
      <Command.Input
        autoFocus
        value={query}
        onValueChange={handleQueryChange}
        placeholder={searchPlaceholder ?? placeholder ?? ''}
        className="w-full border-b border-slate-200 px-3 py-2 text-sm font-body outline-none placeholder:text-slate-400"
      />
      <Command.List id={listboxId} className="max-h-64 overflow-y-auto scrollbar-subtle">
        {loading ? (
          <Command.Loading>
            <div className="px-3 py-4 text-sm text-slate-500">{loadingMessage ?? ''}</div>
          </Command.Loading>
        ) : filteredOptions.length === 0 ? (
          <Command.Empty className="px-3 py-4 text-sm text-slate-500">
            {emptyMessage ?? ''}
          </Command.Empty>
        ) : (
          filteredOptions.map((option) => (
            <ComboboxItem
              key={String(option.value)}
              option={option}
              selected={option.value === value}
              onSelect={handleSelect}
            />
          ))
        )}
      </Command.List>
      {footer ? <div className="border-t border-slate-200 p-2">{footer}</div> : null}
    </Command>
  );

  return (
    <div className={cn('relative flex flex-col gap-1', className)} ref={rootRef}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-body font-medium text-slate-700">
          {label}
          {required && (
            <span className="ml-0.5 text-erro-apoio" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      {portalled ? (
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen} modal={modal}>
          <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              collisionPadding={16}
              onKeyDown={handlePanelKeyDown}
              className={cn(
                'z-[500] w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-content-available-width)] outline-none',
                panelClassName,
              )}
            >
              {panel}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      ) : (
        <>
          {trigger}
          {open && !disabled ? (
            <div
              className={cn('absolute left-0 right-0 top-full z-50 mt-1', panelClassName)}
              onKeyDown={handlePanelKeyDown}
            >
              {panel}
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p id={`${inputId}-error`} className="text-xs text-alamo-red-500">
          {error}
        </p>
      ) : helperText ? (
        <p id={`${inputId}-helper`} className="text-xs text-slate-500">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

interface ComboboxItemProps<T> {
  option: ComboboxOption<T>;
  selected: boolean;
  onSelect: (value: T) => void;
}

function ComboboxItem<T extends string>({
  option,
  selected,
  onSelect,
}: ComboboxItemProps<T>): ReactNode {
  return (
    <Command.Item
      value={option.label}
      disabled={option.disabled}
      onSelect={() => !option.disabled && onSelect(option.value)}
      className={cn(
        'flex cursor-pointer items-start justify-between gap-3 px-3 py-2.5 text-sm font-body',
        'aria-selected:bg-alamo-orange-50 aria-selected:text-alamo-orange-700',
        option.disabled && 'cursor-not-allowed text-slate-400',
      )}
    >
      <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug">
        {option.label}
      </span>
      <span className="flex shrink-0 items-center gap-2 whitespace-nowrap">
        {option.hint && <span className="text-xs font-medium text-slate-500">{option.hint}</span>}
        {selected && <Check className="h-4 w-4 text-alamo-orange-500" />}
      </span>
    </Command.Item>
  );
}
